import log from 'loglevel';
import { generateUUID } from '../services/utils';
import { MessageTypes } from './message-types';

/**
 * Custom user storage for the oidc-client-ts.
 * Synchronizes user objects between cloned/duplicated browser tabs.
 * Synchronization is done over a broadcast channel with a random name shared between the clones.
 * Clones will be unlinked if there is a change of the eService (client-id).
 */
export class TokenStorage implements Storage {
  private readonly KEY_CLONE_INFO = 'oiam-clone-info';
  private readonly KEY_CHANNEL_NAMES = 'oiam-channels';
  private readonly clientid: string;
  private readonly authority: string;
  private broadcastChannel: BroadcastChannel;
  // count of clones for this eService
  // attention:
  //  if the user hits F5 or an error at token refresh occurs (that leads to reload),
  //  the count will not be correct anymore. This hardly can be prevented.
  //  It might lead to unnecessary "token" broadcast messages.
  private cloneCount: number = 0;

  constructor(clientId: string, authority: string) {
    // remember client id, authority for remove foreign/unlink
    this.clientid = clientId;
    this.authority = authority;
    // Channel name map per client id
    const channelNames = this.getChannelsMap();
    if (!channelNames.has(clientId)) {
      // new eService -> generate random channel name
      channelNames.set(clientId, generateUUID());
      sessionStorage.setItem(this.KEY_CHANNEL_NAMES, JSON.stringify([...channelNames]));
    }
    log.debug('oiam-oauth-wc: clone link channel', channelNames.get(clientId));
    this.broadcastChannel = new BroadcastChannel(channelNames.get(clientId));
    this.broadcastChannel.addEventListener('message', (ev: MessageEvent) => this.messageEventHandler(ev));
    // message to update clone counter
    this.broadcastChannel.postMessage({ type: MessageTypes.AM_I_CLONED });
  }

  /**
   * Remove user and channel of the previous eService after eService change.
   * This function is called after successfully got the token on an auth flow.
   */
  unlinkFromClones() {
    const unlink = sessionStorage.getItem(this.KEY_CLONE_INFO);
    if (unlink) {
      const unlinkObj = JSON.parse(unlink);
      // only unlink if different eService
      if (unlinkObj.clientid !== this.clientid) {
        sessionStorage.removeItem(this.KEY_CLONE_INFO);
        // send unlinked message to update clone counters
        const channelNames = this.getChannelsMap();
        if (channelNames.has(unlinkObj.clientid)) {
          const bc = new BroadcastChannel(channelNames.get(unlinkObj.clientid));
          bc.postMessage({ type: MessageTypes.CLONE_UNLINKED });
          // remove previous channel from map
          channelNames.delete(unlinkObj.clientid);
          sessionStorage.setItem(this.KEY_CHANNEL_NAMES, JSON.stringify([...channelNames]));
        }
        // remove the user token of previous eService
        // if user returns to the previous eService, it will be a new auth flow and new token
        // attention:
        //    it is dependent of the implementation of the oidc-client-ts,
        //    format of the key oidc.user:authority:clientid
        const toRemoveUser = 'oidc.user:' + unlinkObj.authority + ':' + unlinkObj.clientid;
        sessionStorage.removeItem(toRemoveUser);
      }
    }
  }

  get length(): number {
    return sessionStorage.length;
  }

  clear(): void {
    sessionStorage.clear();
  }

  getItem(key: string): string | null {
    return sessionStorage.getItem(key);
  }

  key(index: number): string | null {
    return sessionStorage.key(index);
  }

  removeItem(key: string): void {
    sessionStorage.removeItem(key);
  }

  setItem(key: string, value: string): void {
    sessionStorage.setItem(key, value);
    if (this.cloneCount > 0) {
      // send new token to cloned tabs
      this.broadcastChannel.postMessage({
        type: MessageTypes.TOKEN,
        key: key,
        value: value,
      });
    }
  }

  private messageEventHandler(ev: MessageEvent) {
    // handle token refresh event in cloned tabs
    if (ev.data.type === MessageTypes.TOKEN) {
      // update token in cloned tabs
      // use sessionStorage directly (instead of this.setItem) to prevent circular messages/saves
      sessionStorage.setItem(ev.data.key, ev.data.value);
    }
    // handle answer from cloned tabs
    if (ev.data.type === MessageTypes.YOU_ARE_CLONED) {
      this.incrementCounterAndStoreCloneInfo();
      // we only need a link to the current eService, not to eServices previously visited
      // remove all foreign channel names and token
      // attention: it will not work on origin change (e.g. arbeitsagentur.de -> mein-now.de),
      //            because the authority might change in this case
      this.reduceStorageToCurrentUser();
    }
    // handle question from cloned tabs
    if (ev.data.type === MessageTypes.AM_I_CLONED) {
      this.incrementCounterAndStoreCloneInfo();
      // a new clone tab asked for its clones -> answer
      this.broadcastChannel.postMessage({
        type: MessageTypes.YOU_ARE_CLONED,
      });
    }
    // handle unlinked event from cloned tabs
    if (ev.data.type === MessageTypes.CLONE_UNLINKED) {
      // a clone tab was unlinked, decrement clone count
      this.decrementCloneCounter();
    }
  }

  private incrementCounterAndStoreCloneInfo() {
    // there is a new clone, increment clone count
    this.incrementCloneCounter();
    // remember info of clone or base tab to unlink it when navigating away to another eService
    // unlink can be done when other eService has its token
    sessionStorage.setItem(
      this.KEY_CLONE_INFO,
      JSON.stringify({
        clientid: this.clientid,
        authority: this.authority,
      }),
    );
  }

  private reduceStorageToCurrentUser() {
    let channelNames = this.getChannelsMap();
    channelNames.forEach((_value, key) => {
      if (key !== this.clientid) {
        // attention:
        //    it is dependent of the implementation of the oidc-client-ts,
        //    format of the key oidc.user:authority:clientid
        const tokenkey = 'oidc.user:' + this.authority + ':' + key;
        sessionStorage.removeItem(tokenkey);
      }
    });
    channelNames = new Map([...channelNames.entries()].filter(([key]) => key === this.clientid));
    sessionStorage.setItem(this.KEY_CHANNEL_NAMES, JSON.stringify([...channelNames]));
  }

  private getChannelsMap() {
    let channelNames = new Map<string, string>();
    let channelNamesString = sessionStorage.getItem(this.KEY_CHANNEL_NAMES);
    if (channelNamesString) {
      try {
        channelNames = new Map(JSON.parse(channelNamesString));
      } catch (e) {
        log.error('oiam-oauth-wc: error parsing channel names from sessionStorage', e);
      }
    }
    return channelNames;
  }

  private incrementCloneCounter() {
    this.cloneCount++;
    log.debug('oiam-oauth-wc: cloned tabs count', this.cloneCount);
  }

  private decrementCloneCounter() {
    this.cloneCount--;
    log.debug('oiam-oauth-wc: cloned tabs count', this.cloneCount);
    if (this.cloneCount === 0) {
      // no more clones, remove clone info
      sessionStorage.removeItem(this.KEY_CLONE_INFO);
    }
  }
}
